"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Upload, ShieldCheck, Send, BarChart3, LayoutTemplate } from "lucide-react";

const steps = [
    {
        icon: Upload,
        step: "01",
        title: "Import Contacts",
        description: "Bulk upload via CSV/Excel with smart column mapping, duplicate detection, and one-click list assignment.",
        color: "from-blue-500 to-indigo-600",
    },
    {
        icon: ShieldCheck,
        step: "02",
        title: "Verify Emails",
        description: "Deep verification checks syntax, MX records, SMTP, and disposable domains. Skip unsafe emails automatically.",
        color: "from-emerald-500 to-teal-600",
    },
    {
        icon: LayoutTemplate,
        step: "03",
        title: "Create Email Templates",
        description: "Create professional email templates with our drag-and-drop editor. Add your content, images, and branding to create the perfect email for your campaign.",
        color: "from-red-500 to-purple-600",
    },
    {
        icon: Send,
        step: "04",
        title: "Launch Campaign",
        description: "Choose your template, select recipient lists, configure settings, and launch with smart batch processing.",
        color: "from-violet-500 to-purple-600",
    },
    {
        icon: BarChart3,
        step: "05",
        title: "Track Results",
        description: "Watch deliveries, opens, and clicks in real-time. Get detailed analytics on every email sent.",
        color: "from-amber-500 to-orange-600",
    },
];

export default function HowItWorks() {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section id="how-it-works" ref={ref} className="relative py-24 px-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-20"
                >
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-bold uppercase tracking-wider mb-4">
                        How It Works
                    </span>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white tracking-tight">
                        Five Steps to{" "}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400">
                            Perfect Delivery
                        </span>
                    </h2>
                </motion.div>

                {/* Steps */}
                <div className="relative grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
                    {/* Connecting line (desktop) */}
                    <div className="hidden lg:block absolute top-16 left-[8%] right-[8%] h-[2px]">
                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={isInView ? { scaleX: 1 } : {}}
                            transition={{ duration: 1.2, delay: 0.5, ease: "easeInOut" }}
                            className="h-full bg-gradient-to-r from-blue-300 via-emerald-300 via-violet-300 to-amber-300 dark:from-blue-600 dark:via-emerald-600 dark:via-violet-600 dark:to-amber-600 origin-left"
                            style={{ transformOrigin: "left" }}
                        />
                    </div>

                    {steps.map((s, i) => (
                        <motion.div
                            key={s.step}
                            initial={{ opacity: 0, y: 40 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: 0.3 + i * 0.15 }}
                            className="relative flex flex-col items-center text-center"
                        >
                            {/* Step number + icon */}
                            <div className="relative mb-6">
                                <motion.div
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-xl relative z-10`}
                                >
                                    <s.icon className="h-7 w-7 text-white" />
                                </motion.div>
                                <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-800 flex items-center justify-center z-20">
                                    <span className="text-[10px] font-black text-gray-900 dark:text-white">{s.step}</span>
                                </div>
                            </div>

                            {/* Text */}
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
                                {s.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs">
                                {s.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
